import { getRouteCache, hashCoords, setRouteCache } from './map-cache.js';
import { hasValidCoords } from './map-coords.js';

const OSRM_BASE = 'https://router.project-osrm.org';

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

function formatDistance(meters) {
  if (!Number.isFinite(meters) || meters <= 0) return null;
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

/** Fetch driving route polyline + stats from OSRM (public demo server). */
export async function fetchDrivingRoute(coordList, { instanceId = null, dayKey = null } = {}) {
  const valid = coordList.filter(hasValidCoords);
  if (valid.length < 2) {
    return { coords: valid, polyline: valid, distance: null, duration: null };
  }

  const coordHash = hashCoords(valid);
  if (instanceId) {
    const cached = await getRouteCache(instanceId, dayKey, coordHash);
    if (cached) return cached;
  }

  const path = valid.map((c) => `${c.lng},${c.lat}`).join(';');
  const url = `${OSRM_BASE}/route/v1/driving/${path}?overview=full&geometries=geojson`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('route failed');
    const data = await res.json();
    const route = data.routes?.[0];
    if (!route?.geometry?.coordinates?.length) {
      return { coords: valid, polyline: valid, distance: null, duration: null };
    }

    const polyline = route.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
    const result = {
      coords: valid,
      polyline,
      distance: formatDistance(route.distance),
      duration: formatDuration(route.duration),
    };

    if (instanceId) {
      await setRouteCache(instanceId, dayKey, coordHash, result);
    }

    return result;
  } catch {
    return { coords: valid, polyline: valid, distance: null, duration: null };
  }
}
